<script>
const socialImage = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`

export default {
    page() {
        const shouldNoindex = this.$route.matched.some(route => route.meta && route.meta.noindex)
        const meta = [
            { property: 'og:title', vmid: 'og:title', content: `FRISKY` },
            { property: 'og:type', vmid: 'og:type', content: 'website' },
            { property: 'og:url', vmid: 'og:url', content: `${window.location.href}` },
            { property: 'og:image', vmid: 'og:image', content: socialImage },
            { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },
            { property: 'fb:app_id', vmid: 'fb:app_id', content: '126928420652882' },
            { property: 'og:locale', vmid: 'og:locale', content: 'en_US' },
            { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:url', vmid: 'twitter:url', content: `${window.location.href}` },
            { name: 'twitter:site', vmid: 'twitter:site', content: '@friskyradio' },
            { name: 'twitter:creator', vmid: 'twitter:creator', content: '@friskyradio' },
            { name: 'twitter:image', vmid: 'twitter:image', content: socialImage },
        ]

        if (shouldNoindex) {
            meta.push({ name: 'robots', vmid: 'robots', content: 'noindex, nofollow' })
        }

        return {
            // All subcomponent titles will be injected into this template.
            titleTemplate(title) {
                title = typeof title === 'function' ? title(this.$store) : title
                return title ? `${title} | FRISKY` : 'FRISKY'
            },
            meta,
        }
    },

}
</script>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useMessages } from '@composables/use-messages'
import svgSprite from './components/svg-sprite'
import NotificationDialog from './components/notification-dialog'
import ModalManager from './components/modal-manager'
import Logo from './components/logo'
import CustomNativeSmartBanner from './partials/custom-native-smart-banner'
import AppInitializer from './services/app-initializer'
import appLoadTracker from './services/mixpanel/app-load-tracker'
import modalService from './services/modal-service'
import { isInAppBrowser } from './utils/user-agent'
import { features } from './configuration'

const route = useRoute()
const router = useRouter()
const store = useStore()
const getMessageText = useMessages()
const keyControlsService = useKeyControlsService()

const isLoading = ref(false)
const hasRouteInitialized = ref(false)

let appInitializer = null

const loggedIn = computed(() => store.getters['auth/loggedIn'])

const updateFavorites = () => {
    loggedIn.value && store.dispatch('favorites/init')
}

const initializeApp = async () => {
    appInitializer = new AppInitializer({
        store,
        router,
        messages: getMessageText,
        features,
    })
    await appInitializer.initialize()
}

watch(loggedIn, updateFavorites, { immediate: true })

watch(
    () => route.fullPath,
    fullPath => {
        if (route.path === '/' && !hasRouteInitialized.value) return

        const metaTag = document.querySelector('meta[name="apple-itunes-app"]')
        if (metaTag) {
            metaTag.setAttribute('content', `app-id=${import.meta.env.VITE_APPLE_APP_ID}, app-argument=${window.location.origin}${fullPath}`)
        }
    },
    { immediate: true }
)

onMounted(async () => {
    isLoading.value = true
    await initializeApp()
    isLoading.value = false
    window.addEventListener('keydown', keyControlsService.reactToKey)
    // Mark app as ready for TTI tracking
    appLoadTracker.markReady()

    if (isInAppBrowser()) {
        modalService.show('IN_APP_BROWSER_NOTICE')
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keyControlsService.reactToKey)
    if (appInitializer) appInitializer.destroy()
})
</script>

<template>
    <div id="app">
        <svg-sprite />
        <div
            v-if="isLoading"
            class="app-content-loader"
        >
            <Logo size="large" />
        </div>
        <router-view
            v-else
            :key="$route.name + ($route.params.id || $route.params.channelId || '')"
        />
        <NotificationDialog v-if="$store.state.appearance.isNotificationOpen" />
        <ModalManager />
        <CustomNativeSmartBanner/>
    </div>
</template>

<style lang="scss">
@use '@design' as *;

@import '@design/animations';
@import '@design/normalize';
@import '@design/utilities';
@import 'nprogress/nprogress.css';

@include init;

*,
*::before,
*::after {
    box-sizing: border-box;
}

::-webkit-scrollbar {
    width: $size-scrollbar-width;
}

::-webkit-scrollbar-track {
    opacity: 0;
}

::-webkit-scrollbar-thumb {
    min-height: $s-xxl;
    background-color: $c-tuna;
    background-clip: padding-box;
    border: 4px solid transparent;
    border-radius: 16px;
}

/* stylelint-disable selector-max-type -- app root: global element resets */
html {
    scroll-behavior: smooth;
}

body {
    width: 100vw;
    height: 100vh;
    background: $c-body-bg;

    &:has([is-nav-open="true"]) {
        overflow: hidden;
    }
}

option {
    color: $c-white;
    background: $c-grey-woodsmoke;
}

@include desktop {
    body {
        &:has([is-nav-open="true"]) {
            overflow: unset;
        }
    }
}
/* stylelint-enable selector-max-type */

.app-content-loader {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.app-loader-logo {
    animation: fade 1.7s infinite alternate ease-in-out;
}

// ===
// Vendor
// ===

#nprogress .bar {
    height: 5px;
    background: $c-link-text;
    will-change: transform;
}

#nprogress .peg {
    box-shadow: 0 0 10px $c-link-text, 0 0 5px $c-link-text;
}
</style>
